import React, { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';
import { useEffect } from 'react';
import { MdClose } from 'react-icons/md';

import Input, { SubmitButton } from './input';
import ModalGeneric from '../modalNew';
import { CONSENT_TEXT, FORMS_ENDPOINT } from '../../config/forms';
import { useSubmissionId } from '../../hooks/useSubmissionId';

export default function Subscribe(props: {
  isOpen: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [failureText, setFailureText] = useState('');
  const [successText, setSuccessText] = useState('');
  const { submissionId, renew } = useSubmissionId(props.isOpen);
  const focusRefs: {
    [prop: string]: React.RefObject<HTMLInputElement>;
  } = {};

  useEffect(() => {
    const interval = setInterval(() => {
      setFailureText('');
    }, 4000);
    return () => clearInterval(interval);
  }, [failureText]);

  useEffect(() => {
    const interval = setInterval(() => {
      setSuccessText('');
    }, 4000);
    return () => clearInterval(interval);
  }, [successText]);

  const validateEmail = (email: string) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  const validateData: () => boolean = () => {
    if (firstName.trim() === '') {
      setFailureText('Please enter your first name');
      setIsBusy(false);
      return false;
    }
    if (lastName.trim() === '') {
      setFailureText('Please enter your last name');
      setIsBusy(false);
      return false;
    }
    if (!validateEmail(email)) {
      setFailureText('Please enter a valid email address');
      setIsBusy(false);
      return false;
    }
    return true;
  };
  // Form Submit
  const handleSubmit = async () => {
    setIsBusy(true);
    if (!validateData()) {
      return;
    }
    // Stop the form from submitting and refreshing the page.
    const JSONdata = JSON.stringify({
      email: email,
      first_name: firstName,
      last_name: lastName,
      event_type: 'subscribe',
      /* Resent unchanged on every retry, so a double-click stores one event. */
      submission_id: submissionId,
      consent_text: CONSENT_TEXT,
    });
    const endpoint = FORMS_ENDPOINT;
    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSONdata,
    };
    try {
      const response = await fetch(endpoint, options);
      if (response.status === 200) {
        setSuccessText(
          'Success. You have been successfully added to our mailing list'
        );
        renew();
      } else {
        setFailureText(
          'Apologies. Your request to join the mailing list encountered an error. Please ask to be added at info@kumargallery.com'
        );
      }
    } catch (err) {
      /* A network failure, a CORS-rejected error response, an aborted request:
         all land here. Without this the promise rejects unhandled and the
         button stays on "Please wait" forever. */
      setFailureText(
        'Apologies. Your request could not be sent. Please try again, or ask to be added at info@kumargallery.com'
      );
    } finally {
      setIsBusy(false);
    }
  };

  ['firstName', 'lastName', 'email', 'message'].forEach(
    (k) => (focusRefs[k] = React.createRef())
  );
  return (
    <ModalGeneric
      isOpen={props.isOpen}
      closeModal={() => {
        props.setOpen(false);
      }}
    >
      <div className='modal-heading'>
        <h2 className='modal-title'>Join our Mailing List</h2>
        <button
          type='button'
          aria-label='Close'
          className='modal-close'
          onClick={() => props.setOpen(false)}
        >
          <MdClose size={20} />
        </button>
      </div>
      <div className='modal-body'>
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
          <Input
            name='First Name'
            label='First Name (*)'
            value={firstName}
            handleChange={(e) => {
              if (e.target !== null) {
                setFirstName((e.target as HTMLInputElement).value);
              }
            }}
            fref={focusRefs['firstName']}
          />
          <Input
            name='Last Name'
            label='Last Name (*)'
            value={lastName}
            handleChange={(e) => {
              if (e.target !== null) {
                setLastName((e.target as HTMLInputElement).value);
              }
            }}
            fref={focusRefs['lastName']}
          />
        </div>
        <div className='mt-4'>
          <Input
            name='Email'
            label='Email (*)'
            value={email}
            handleChange={(e) => {
              if (e.target !== null) {
                setEmail((e.target as HTMLInputElement).value);
              }
            }}
            fref={focusRefs['email']}
            placeholder=''
          />
        </div>
        <div className='mt-6 flex flex-col items-center'>
          <button
            type='submit'
            className={SubmitButton}
            disabled={isBusy}
            onClick={() => handleSubmit()}
          >
            {!isBusy ? 'Subscribe' : 'Please wait'}
          </button>
          {/* Space kept whether or not there is a message, so the reply does
              not shove the button around as it arrives. */}
          <div className='modal-message'>
            {successText && (
              <span className='text-green-700'>{successText}</span>
            )}
            {failureText && <span className='text-red-600'>{failureText}</span>}
          </div>
        </div>
      </div>
    </ModalGeneric>
  );
}
